import React, { useEffect, useState, useRef } from "react";
import { Box, Button } from "@mui/material";
import { useQuery } from "contexts";
import { export_alerts_csv } from "queries";
import "src/css/ExportAlertsButton.css";

function csvEscape(v) {
    const s = v == null ? "" : String(v).replace(/\r?\n/g, " ");
    return `"${s.replace(/"/g, '""')}"`;
}

function rowsToCSV(rows) {
    const header = [
        "id", "priority", "displayName", "displayType", "alertClass", "status", "text", "requiresAck", "occuredAt",
        "position.lat", "position.lon",
        "vehicle.id", "vehicle.displayName", "vehicle.lat", "vehicle.lon", "vehicle.routeState"
    ];
    const lines = [header.join(",")];

    for (const r of rows) {
        const p = r.position || {};
        const v = r.vehicle || {};
        lines.push([
            csvEscape(r.id),
            csvEscape(r.priority),
            csvEscape(r.displayName),
            csvEscape(r.displayType),
            csvEscape(r.alertClass),
            csvEscape(r.status),
            csvEscape(r.text),
            csvEscape(r.requiresAck),
            csvEscape(r.occuredAt),
            csvEscape(p.lat),
            csvEscape(p.lon),
            csvEscape(v.id),
            csvEscape(v.displayName),
            csvEscape(v.lat),
            csvEscape(v.lon),
            csvEscape(v.routeState),
        ].join(","));
    }
    return lines.join("\r\n");
}

export default function ExportAlertsButton({ defaultHours = 168, size = 30000, onExportCSV, exporting: exportingProp, exportError }) {
    const DAYS_IN_HOURS = 24;
    const MAX_DAYS = defaultHours / DAYS_IN_HOURS;

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const toLocalInput = (d) => {
        const t = new Date(d);
        return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
    };
    const toISO = (localStr) => new Date(localStr).toISOString();

    const [startLocal, setStartLocal] = useState(toLocalInput(now.getTime() - defaultHours * 3600 * 1000));
    const [endLocal, setEndLocal] = useState(toLocalInput(now));
    const [exportingLocal, setExportingLocal] = useState(false);
    const exporting = exportingProp !== undefined ? exportingProp : exportingLocal;
    const [warning, setWarning] = useState("");
    const lastDataRef = useRef(null);

    const { data, loading, error, refetchData } = useQuery(export_alerts_csv, {
        enable: false,
    });

    const getHourDiff = () => {
        const start = new Date(startLocal);
        const end = new Date(endLocal);
        return (end - start) / (1000 * 60 * 60);
    };

    useEffect(() => {
        if (!startLocal || !endLocal) {
            setWarning("");
            return;
        }
        const start = new Date(startLocal);
        const end = new Date(endLocal);

        if (start > end) {
            setWarning("⚠️ Start date/time must be before end date/time.");
        } else if (getHourDiff() > defaultHours) {
            setWarning(`⚠️ Please select a date range of ${MAX_DAYS} days or less.`);
        } else {
            setWarning("");
        }
    }, [startLocal, endLocal, defaultHours]);

    useEffect(() => {
        if (!exportingLocal || !data) return;
        if (lastDataRef.current === data) return;
        const blob = new Blob([rowsToCSV(data.exportAlerts || [])], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        const d0 = startLocal.slice(0, 10);
        const d1 = endLocal.slice(0, 10);
        a.href = url;
        a.download = `alerts_${d0}_${d1}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        lastDataRef.current = data;
        setExportingLocal(false);
    }, [data, startLocal, endLocal, exportingLocal]);

    const onExport = () => {
        const start = new Date(startLocal);
        const end = new Date(endLocal);
        if (!startLocal || !endLocal || getHourDiff() > defaultHours || start > end) return;
        if (onExportCSV) {
            if (!exporting) {
                onExportCSV({
                    startLocal,
                    endLocal,
                    setExporting: exportingProp !== undefined ? () => { } : setExportingLocal,
                });
            }
        } else {
            setExportingLocal(true);
            refetchData(
                {
                    size: size,
                    filter: {
                        createdBetween: {
                            from: toISO(startLocal),
                            to: toISO(endLocal)
                        }
                    }
                },
                true
            );
        }
    };

    const handleStartChange = (e) => {
        const newStart = e.target.value;
        setStartLocal(newStart);
        const endDate = new Date(newStart);
        endDate.setDate(endDate.getDate() + MAX_DAYS);
        setEndLocal(toLocalInput(endDate));
    };

    const handleEndChange = (e) => {
        const newEnd = e.target.value;
        setEndLocal(newEnd);
        const startDate = new Date(newEnd);
        startDate.setDate(startDate.getDate() - MAX_DAYS);
        setStartLocal(toLocalInput(startDate));
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1 }}>
            <input
                type="datetime-local"
                value={startLocal}
                onChange={handleStartChange}
            />
            <input
                type="datetime-local"
                value={endLocal}
                onChange={handleEndChange}
            />
            <Button
                variant="outlined"
                onClick={onExport}
                disabled={
                    exporting ||
                    !startLocal ||
                    !endLocal ||
                    getHourDiff() > defaultHours ||
                    new Date(startLocal) > new Date(endLocal)
                }
            >
                {exporting ? "Exporting…" : "Download CSV"}
            </Button>
            {warning && <span className="warning-animated">{warning}</span>}
            {(exportError || error) && <span style={{ color: "crimson", marginLeft: 8 }}>Export error</span>}
        </Box>
    );
}